import React from "react";
import {
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_SELECT_MODE_2,
  FILTER_TYPE_SELECT_MODE_3,
  FILTER_TYPE_STRING
} from "../../../components/SmartTable/methods";
import TableCell from "../../../components/SmartTable/TableCell";
import Tag from "../../../components/Tag";
import { formatString } from "../../../utils/common";
import ShowMoreList from "../../../components/ShowMoreList";
import ActivityActionColumn from "./ActivityActionColumn";
import { getActivityStageLangKeyName } from "./methods";

/**
 * Builds and returns the column configuration array for the QAM activities SmartTable.
 *
 * Each column includes sorting, filtering, and a custom cell renderer. The "entity" column
 * uses different data depending on the activity stage (closed activities expose entity_name
 * instead of entity due to the way archived records are stored on the backend).
 *
 * @param {object}   permission    - Permission helper object (canDo / hasModule checks)
 * @param {object}   loggedUser    - Currently authenticated user record
 * @param {string}   language      - Active locale code (e.g. "en", "fr")
 * @param {Function} trans         - i18n translation function
 * @param {Function} formatLocale  - Date formatter respecting the user's locale
 * @param {string}   dateFormat    - Company date format string (e.g. "DD/MM/YYYY")
 * @param {boolean}  isTablet      - Whether the current viewport is tablet-sized
 * @returns {object[]} Array of Ant Design Table column definition objects
 */
export const generateColumns = (permission, loggedUser, language, trans, formatLocale, dateFormat, isTablet) => {
  return [
    {
      title: "qam.activities.stage",
      dataIndex: "stage_name",
      sorter: true,
      width: 130,
      filterType: FILTER_TYPE_SELECT_MODE_2,
      filterProps: {
        apiRoute: "qam/stages",
        transformer: (stages) => {
          return [
            {
              text: trans("qam.activities.all_stages_without_archived"),
              transKey: "qam.activities.all_stages_without_archived",
              name: "__Closed",
              id: "__Closed",
              value: "__Closed"
            },
            ...stages.map((item) => {
              const stageTranslationKey = getActivityStageLangKeyName(item.name);
              return {
                ...item,
                id: item.name,
                value: item.name,
                text: trans(stageTranslationKey),
                transKey: stageTranslationKey
              };
            })
          ];
        }
      },
      render: (text, row) => {
        const stageLangKeyName = getActivityStageLangKeyName(row.stage_name);
        return (
          <TableCell>
            {stageLangKeyName ? (
              <Tag style={{ background: row.stage_color, color: "#fff", border: 0 }}>{trans(stageLangKeyName)}</Tag>
            ) : (
              "-"
            )}
          </TableCell>
        );
      }
    },
    {
      title: "qam.activities.id",
      dataIndex: "number",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      width: 130,
      render: (text, row) => <TableCell minWidth={130}>{formatString(row.number, "-")}</TableCell>
    },
    {
      title: "container.general_words.title",
      dataIndex: "title",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      render: (text, row) => <TableCell minWidth={130}>{formatString(row.title, "-")}</TableCell>
    },
    {
      title: "qam.activities.scope",
      dataIndex: "scope",
      filterType: FILTER_TYPE_SELECT_MODE_3,
      filterNameKey: "scopes",
      filterProps: {
        apiRoute: "qam/scopes?order_by_column%5B%5D=name&order_by_type%5B%5D=asc",
        searchColumns: ["name"]
      },
      render: (text, row) => (
        <TableCell minWidth={130}>
          {row.scopes?.length > 0
            ? row.scopes.map((scope) => (
                <Tag key={scope.id} className="tag-secondary">
                  {scope.name}
                </Tag>
              ))
            : "-"}
        </TableCell>
      )
    },
    {
      title: "qam.activities.entity",
      dataIndex: "entity",
      sorter: true,
      filterType: FILTER_TYPE_STRING,
      render: (text, row) => (
        <TableCell minWidth={130}>
          {formatString(row.stage_name === "Closed" ? row?.entity_name : row?.entity, "-")}
        </TableCell>
      )
    },
    {
      title: "qam.activities.timeline",
      dataIndex: "timeline_date",
      sorter: true,
      filterType: FILTER_TYPE_DATE_RANGE,
      width: 170,
      render: (text, row) => {
        const formattedDates = row.auditdates.map((item) => {
          return formatLocale({ date: item.date, dateFormat: item.hour ? `${dateFormat} HH:mm` : dateFormat });
        });
        return (
          <TableCell minWidth={130}>
            <ShowMoreList data={formattedDates} />
          </TableCell>
        );
      }
    },
    {
      title: trans("container.general_words.actions"),
      fixed: "right",
      width: isTablet ? 80 : 160,
      onCell: () => ({
        style: { right: "-5px" }
      }),
      render: (row = {}) => <ActivityActionColumn row={row} />
    }
  ];
};
